import { Button } from '@client/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@design-system';
import { FullScreenLoadingOverlay } from '@client/components';
import { ClientHomeTopBar } from '@client/components/ClientHomeTopBar';
import { PartnerLinkRequestModal } from '@client/components/PartnerLinkRequestModal';
import { OnboardingOverview } from '@client/pages/OnboardingOverview';

import { MoveUpRight, Users, RefreshCw, AlertTriangle } from 'lucide-react';

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import { useNavigateToPage } from '@client/Router';
import { useDispatch, useSelector } from 'react-redux';
import { useClientSignOut } from '@client/components/hooks/use-sign-out';
import { useAdvisorAccountEntry } from '@client/features/advisor-account-activation/use-advisor-account-entry';
import { isAdvisorAccountActivationEntryEnabled } from '@client/features/advisor-account-activation/is-advisor-account-activation-enabled';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { IntakeChangeRequestBanner } from '@client/features/intake-change-request/intake-change-request-banner';

import { format } from 'date-fns';
import { postPartnerLinkRequestUpdateStatusRequest } from '@client';
import { selectSession } from '@client/model/selectors';
import { selectClientProfiles, selectClientsIsLoading } from '@client/model/selectors';
import { forgetConsumerFlowVisit } from '@/features/consumer';

export function ClientHome() {
  const clientProfiles = useSelector(selectClientProfiles);
  const clientsIsLoading = useSelector(selectClientsIsLoading);
  const session = useSelector(selectSession);
  const { isClerkLoading, clerkEmail } = useClerkClientAuth();
  const { flags } = useFeatureFlags();

  const handleSignOut = useClientSignOut();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const advisorAccountEntry = useAdvisorAccountEntry({
    enabled: isAdvisorAccountActivationEntryEnabled({
      clientProfiles,
      clientRolloutEnabled: flags.advisorAccountActivation === true,
    }),
    advisorAccount: session.advisorAccount,
  });
  const continueButtonRef = useRef<HTMLButtonElement>(null);

  const [partnerRequestModal, setPartnerRequestModal] = useState<{
    client: ClientProfile;
    action: 'accept' | 'decline';
  } | null>(null);

  const handleAcceptPartnerRequest = async (client: ClientProfile) => {
    setPartnerRequestModal({ client, action: 'accept' });
  };

  const handleDeclinePartnerRequest = async (client: ClientProfile) => {
    setPartnerRequestModal({ client, action: 'decline' });
  };

  const handleProcessRequest = async (action: 'accept' | 'decline') => {
    const requestingClient = partnerRequestModal?.client.partnerLinkRequestClient;
    const client = partnerRequestModal?.client;
    if (!requestingClient || !client) return;

    try {
      dispatch(
        postPartnerLinkRequestUpdateStatusRequest({
          clientId: requestingClient.clientId,
          partnerClientId: client.clientId,
          status: action === 'accept' ? 'accepted' : 'declined',
        }),
      );
    } catch (error) {
      console.error('Error processing partner request:', error);
      throw error;
    }
  };

  if (clientsIsLoading || isClerkLoading) {
    return <FullScreenLoadingOverlay isLoading={true} />;
  }

  return (
    <>
      <ClientHomeTopBar
        handleClickSignOut={handleSignOut}
        clientEmail={clerkEmail || ''}
        clientProfiles={clientProfiles ?? []}
        onAcceptPartnerRequest={handleAcceptPartnerRequest}
        onDeclinePartnerRequest={handleDeclinePartnerRequest}
        advisorAccountAction={advisorAccountEntry.advisorAccountAction}
      />
      <div className="flex min-h-[calc(100vh-100px)] flex-col items-center justify-start py-12">
        {clientProfiles && (
          <div className="w-full max-w-5xl px-4">
            {clientProfiles.map(profile => (
              <IntakeChangeRequestBanner
                key={profile.clientId}
                clientId={profile.clientId}
              />
            ))}
          </div>
        )}
        <div className="w-full max-w-3xl px-4">
          <PageHeader />
          {clientProfiles ? <ClientList clients={clientProfiles} /> : <ClientProfilesEmptyState />}
        </div>
      </div>

      <Dialog
        open={advisorAccountEntry.isConfirmationOpen}
        onOpenChange={advisorAccountEntry.setConfirmationOpen}
      >
        <DialogContent
          className="gap-6 sm:max-w-lg"
          showCloseButton={false}
          onOpenAutoFocus={event => {
            event.preventDefault();
            continueButtonRef.current?.focus();
          }}
        >
          <DialogHeader className="gap-2">
            <DialogTitle className="text-xl">Create an advisor account</DialogTitle>
            <DialogDescription className="text-sm leading-6 text-gray-600">
              Advisor accounts are for people who plan long-term care for others as part of their
              work: financial professionals, advisors and agents, estate planners, and benefits
              teams.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => advisorAccountEntry.setConfirmationOpen(false)}
            >
              Cancel
            </Button>
            <Button
              ref={continueButtonRef}
              type="button"
              onClick={() =>
                navigate('/client/advisor-activation', {
                  state: {
                    advisorActivationConfirmed: true,
                    advisorActivationPreflight: advisorAccountEntry.preflight,
                  },
                })
              }
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partner Request Modal */}
      {partnerRequestModal && (
        <PartnerLinkRequestModal
          open={true}
          onClose={() => setPartnerRequestModal(null)}
          clientProfile={partnerRequestModal.client}
          action={partnerRequestModal.action}
          onAccept={() => handleProcessRequest('accept')}
          onDecline={() => handleProcessRequest('decline')}
        />
      )}
    </>
  );
}

function PageHeader() {
  return (
    <div className="mb-8">
      <h1 className="text-primary-dark mb-2 text-3xl font-medium">Select a profile</h1>
      <p className="text-lg text-gray-700">Which profile would you like to access?</p>
    </div>
  );
}

interface ClientListProps {
  clients: ClientProfile[];
}

function ClientList({ clients }: ClientListProps) {
  return (
    <div className="w-full max-w-3xl space-y-4">
      {clients.map(client => (
        <ClientCard
          key={client.clientId}
          client={client}
        />
      ))}
    </div>
  );
}

interface ClientCardProps {
  client: ClientProfile;
}

function ClientCard({ client }: ClientCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const navigateToPage = useNavigateToPage();

  const handleSelectClient = () => {
    forgetConsumerFlowVisit(client.clientId);
    navigateToPage(OnboardingOverview, { clientId: client.clientId });
  };
  const hasPartnerRequest = !!client.partnerLinkRequestClient;

  return (
    <div
      className={`relative cursor-pointer rounded-lg border p-6 ${
        hasPartnerRequest ? ' bg-primary-50' : isHovered ? ' border-primary-700' : 'border-gray-200'
      } transition-colors duration-200`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleSelectClient}
    >
      {hasPartnerRequest && (
        <div className="bg-primary-dark absolute -right-2 -top-3 flex items-center space-x-1 rounded-full px-3 py-1 text-xs font-medium text-white shadow-md">
          <AlertTriangle className="h-3 w-3" />
          <span>Partner Request</span>
        </div>
      )}

      <div className="flex items-center">
        <Avatar
          firstName={client.clientFirstName}
          lastName={client.clientLastName}
        />

        <ClientInfo client={client} />

        {isHovered && <SelectButton onClick={handleSelectClient} />}
      </div>

      {/* Partner Request Info */}
      {hasPartnerRequest && (
        <div className="border-primary-200 mt-4 rounded-md border bg-white/80 p-3">
          <p className="text-primary-800 text-primary-dark text-sm">
            <span className="font-medium">
              {client.partnerLinkRequestClient!.clientFirstName}{' '}
              {client.partnerLinkRequestClient!.clientLastName}
            </span>{' '}
            has requested to link accounts.{' '}
            <span className="text-primary-dark font-medium">
              Check your notifications to respond.
            </span>
          </p>
        </div>
      )}
    </div>
  );
}

interface ClientInfoProps {
  client: ClientProfile;
}

function ClientInfo({ client }: ClientInfoProps) {
  return (
    <div className="ml-4 grow text-base">
      <h2 className="text-primary-dark text-lg font-semibold">
        {client.clientFirstName} {client.clientLastName}
      </h2>
      <p className="text-gray-700">
        Created on {format(client.clientAddedDateTime, 'MMMM d, yyyy')}
      </p>
      <p className="text-gray-700">
        Advisor:{' '}
        <span className=" text-primary-dark">
          {client.clientAdvisor.advisorFirstName} {client.clientAdvisor.advisorLastName}
        </span>
        {' @ '}
        <span className=" text-primary-dark">{client.clientAdvisor.organizationDisplayName}</span>
      </p>
    </div>
  );
}

function SelectButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      className="absolute right-4 top-4 rounded-full"
      size="icon"
      aria-label="Select client"
      onClick={onClick}
    >
      <MoveUpRight className="h-5 w-5" />
    </Button>
  );
}

interface AvatarProps {
  firstName: string;
  lastName: string;
}

function Avatar({ firstName, lastName }: AvatarProps) {
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`;

  return (
    <div className="bg-primary-100 flex h-14 w-14 shrink-0 items-center justify-center rounded-full">
      <span className="text-primary-dark  text-xl">{initials}</span>
    </div>
  );
}

function ClientProfilesEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-6 py-12">
      <div className="rounded-full bg-gray-100 p-3">
        <Users className="h-8 w-8 text-gray-500" />
      </div>
      <h3 className="mt-4 text-lg font-medium text-gray-900">No Client Profiles Yet</h3>
      <p className="mt-2 max-w-sm text-center text-gray-700">
        It looks like there aren't any client profiles associated with your account yet. Please
        contact support if you believe this is an error.
      </p>
      <div className="mt-6 flex gap-3">
        <Button
          variant="outline"
          className="inline-flex items-center gap-2"
          onClick={() => window.location.reload()}
        >
          <RefreshCw className="h-4 w-4" />
          Refresh Page
        </Button>
      </div>
    </div>
  );
}
